import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Chip, IconButton, Alert,
  CircularProgress, Card, CardContent, CardActions, MenuItem, Select,
  FormControl, InputLabel, List, ListItem, ListItemText, ListItemAvatar,
  Avatar, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import api from '../../services/api';

export default function ClassesTab({ schoolId }) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editClass, setEditClass] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [formData, setFormData] = useState({ name: '', grade: 10 });
  const [saving, setSaving] = useState(false);

  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/schools/${schoolId}/classes`);
      setClasses(res.data.data || res.data.classes || []);
    } catch (err) {
      setError('Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post(`/api/schools/${schoolId}/classes`, formData);
      setCreateOpen(false);
      setFormData({ name: '', grade: 10 });
      fetchClasses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create class');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      await api.patch(`/api/schools/${schoolId}/classes/${editClass._id}`, formData);
      setEditClass(null);
      setFormData({ name: '', grade: 10 });
      fetchClasses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update class');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await api.delete(`/api/schools/${schoolId}/classes/${classId}`);
      fetchClasses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete class');
    }
  };

  const fetchClassDetail = async (classId) => {
    try {
      const res = await api.get(`/api/schools/${schoolId}/classes/${classId}`);
      setSelectedClass(res.data.data || res.data);
    } catch (err) {
      setError('Failed to load class details');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Classes ({classes.length})</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setFormData({ name: '', grade: 10 }); setCreateOpen(true); }} size="small">
          New Class
        </Button>
      </Box>

      <Grid container spacing={2}>
        {classes.map(cls => (
          <Grid item xs={12} sm={6} md={4} key={cls._id}>
            <Card variant="outlined" sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => fetchClassDetail(cls._id)}>
              <CardContent sx={{ pb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">{cls.name}</Typography>
                <Typography variant="body2" color="text.secondary">Grade {cls.grade}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip icon={<PeopleIcon />} label={`${cls.learnerCount || cls.learners?.length || 0} learners`} size="small" variant="outlined" />
                  {cls.averageAccuracy !== undefined && (
                    <Chip label={`${cls.averageAccuracy}% avg`} size="small"
                      color={cls.averageAccuracy >= 60 ? 'success' : cls.averageAccuracy >= 40 ? 'warning' : 'error'}
                      variant="outlined" />
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setFormData({ name: cls.name, grade: cls.grade }); setEditClass(cls); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(cls._id); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {classes.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No classes yet. Create your first class to get started.</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {selectedClass && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{selectedClass.name || selectedClass.class?.name} — Learners</Typography>
            <Button size="small" onClick={() => setSelectedClass(null)}>Close</Button>
          </Box>
          <List dense>
            {(selectedClass.learners || []).map((l, i) => (
              <ListItem key={l._id || i}>
                <ListItemAvatar><Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>{(l.username || '?')[0].toUpperCase()}</Avatar></ListItemAvatar>
                <ListItemText primary={l.username || l.profile?.name || 'Unknown'} secondary={l.profile?.grade ? `Grade ${l.profile.grade}` : ''} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItem>
            ))}
            {(selectedClass.learners || []).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No learners in this class yet.</Typography>
            )}
          </List>
        </Paper>
      )}

      <Dialog open={createOpen || !!editClass} onClose={() => { setCreateOpen(false); setEditClass(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{editClass ? 'Edit Class' : 'Create New Class'}</DialogTitle>
        <DialogContent>
          <TextField label="Class Name" fullWidth margin="normal" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Grade 10A" />
          <FormControl fullWidth margin="normal">
            <InputLabel>Grade</InputLabel>
            <Select value={formData.grade} label="Grade" onChange={e => setFormData(p => ({ ...p, grade: e.target.value }))}>
              {[8, 9, 10, 11, 12].map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateOpen(false); setEditClass(null); }}>Cancel</Button>
          <Button variant="contained" disabled={!formData.name.trim() || saving} onClick={editClass ? handleUpdate : handleCreate}>
            {saving ? <CircularProgress size={20} /> : (editClass ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
